class HomepageHeroMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.homepage-hero__track');
    this.primaryGroup = this.querySelector('.homepage-hero__group');
    this.toggle = this.querySelector('.homepage-hero__motion-toggle');

    if (!this.classList.contains('homepage-hero__media--animated') || !this.track || !this.primaryGroup) return;

    this.toggleMotion = () => {
      const isPaused = this.dataset.paused !== 'true';

      this.dataset.paused = String(isPaused);
      this.toggle?.setAttribute('aria-pressed', String(isPaused));
      this.toggle?.setAttribute('aria-label', isPaused ? 'Play gallery animation' : 'Pause gallery animation');
    };

    this.updateMotion = () => {
      const trackStyles = getComputedStyle(this.track);
      const gap = Number.parseFloat(trackStyles.columnGap) || 0;
      const distance = this.primaryGroup.getBoundingClientRect().width + gap;
      const speed = Number(this.dataset.speed) || 25;

      if (distance <= 0) return;

      this.style.setProperty('--homepage-hero-offset', `${distance * -1}px`);
      this.style.setProperty('--homepage-hero-duration', `${distance / speed}s`);
      this.dataset.ready = 'true';
    };

    this.toggle?.addEventListener('click', this.toggleMotion);
    this.resizeObserver = new ResizeObserver(this.updateMotion);
    this.resizeObserver.observe(this.primaryGroup);
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.dataset.inView = String(entry.isIntersecting);
    });
    this.intersectionObserver.observe(this);
    this.motionFrame = window.requestAnimationFrame(this.updateMotion);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.toggleMotion);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    if (this.motionFrame) window.cancelAnimationFrame(this.motionFrame);
  }
}

class HomepageCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) {
      this.resizeObserver?.observe(this.track);
      this.addEventListeners();
      this.updateNavigation();
      return;
    }

    this.initialized = true;
    this.track = this.querySelector('[data-homepage-carousel-track]');
    this.previousButton = this.querySelector('[data-homepage-carousel-previous]');
    this.nextButton = this.querySelector('[data-homepage-carousel-next]');
    this.status = this.querySelector('[data-homepage-carousel-status]');

    if (!this.track || !this.previousButton || !this.nextButton) return;

    this.handlePreviousClick = () => this.scroll(-1);
    this.handleNextClick = () => this.scroll(1);
    this.handleScroll = () => this.scheduleNavigationUpdate();

    this.addEventListeners();
    this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
    this.resizeObserver.observe(this.track);
    this.updateNavigation();
  }

  disconnectedCallback() {
    this.eventController?.abort();
    this.disconnectDrag?.();
    this.resizeObserver?.disconnect();
    if (this.navigationFrame) window.cancelAnimationFrame(this.navigationFrame);
  }

  addEventListeners() {
    this.eventController?.abort();
    this.eventController = new AbortController();
    const { signal } = this.eventController;

    this.previousButton.addEventListener('click', this.handlePreviousClick, { signal });
    this.nextButton.addEventListener('click', this.handleNextClick, { signal });
    this.track.addEventListener('scroll', this.handleScroll, { signal });
    this.disconnectDrag?.();
    this.disconnectDrag = window.MXTheme.initializeHorizontalDragScroll(this.track, {
      onDragEnd: ({ scrollLeft }) => {
        const positions = this.getScrollPositions();
        const targetPosition = positions.reduce((closest, position) =>
          Math.abs(position - scrollLeft) < Math.abs(closest - scrollLeft) ? position : closest
        );

        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.track.scrollTo({ left: targetPosition, behavior });
      },
    });
  }

  scheduleNavigationUpdate() {
    if (this.navigationFrame) return;

    this.navigationFrame = window.requestAnimationFrame(() => {
      this.navigationFrame = null;
      this.updateNavigation();
    });
  }

  getScrollPositions() {
    const items = Array.from(this.track.querySelectorAll('.homepage-carousel__item'));
    const firstItem = items[0];
    if (!firstItem) return [0];

    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const firstOffset = firstItem.offsetLeft;
    const positions = [];

    for (const item of items) {
      const position = Math.min(maxScroll, Math.max(0, item.offsetLeft - firstOffset));
      const previousPosition = positions[positions.length - 1];

      if (previousPosition === undefined || Math.abs(position - previousPosition) > 1) {
        positions.push(position);
      }
    }

    return positions.length > 0 ? positions : [0];
  }

  scroll(direction) {
    const positions = this.getScrollPositions();
    const currentPosition = this.track.scrollLeft;
    const targetPosition =
      direction > 0
        ? (positions.find((position) => position > currentPosition + 1) ?? positions[positions.length - 1])
        : (positions.slice().reverse().find((position) => position < currentPosition - 1) ?? positions[0]);

    this.track.scrollTo({ left: targetPosition, behavior: 'smooth' });
  }

  updateNavigation() {
    if (!this.track || !this.previousButton || !this.nextButton) return;

    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const positions = this.getScrollPositions();
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    let currentIndex = 0;

    for (let index = 1; index < positions.length; index += 1) {
      if (Math.abs(positions[index] - this.track.scrollLeft) < Math.abs(positions[currentIndex] - this.track.scrollLeft)) {
        currentIndex = index;
      }
    }

    const total = positions.length;
    const current = atEnd ? total : currentIndex + 1;
    this.previousButton.disabled = this.track.scrollLeft <= 1;
    this.nextButton.disabled = atEnd;

    if (this.status) {
      this.status.textContent = `${current} of ${total}`;
    }
  }
}

class HomepageTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.tabs = Array.from(this.querySelectorAll('[data-homepage-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-homepage-tab-panel]'));

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.activate(tab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    }
  }

  activate(selectedTab, moveFocus = false) {
    for (const tab of this.tabs) {
      const selected = tab === selectedTab;
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);

      tab.setAttribute('aria-selected', selected.toString());
      tab.tabIndex = selected ? 0 : -1;
      if (panel) panel.hidden = !selected;
    }

    if (moveFocus) selectedTab.focus();

    const activePanel = this.querySelector(`#${CSS.escape(selectedTab.getAttribute('aria-controls'))}`);
    activePanel?.querySelector('homepage-carousel')?.updateNavigation();
  }

  onKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let nextIndex;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.activate(this.tabs[nextIndex], true);
  }
}

class HomepageRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.renderRecommendation();
  }

  renderRecommendation() {
    const templates = Array.from(this.querySelectorAll('template[data-homepage-collection-handle]'));
    const fallback = templates.find((template) => template.dataset.homepageFallback === 'true') ?? templates[0];
    const candidates = templates.filter((template) => template.dataset.homepageFallback !== 'true');
    const selected = this.selectCollection(candidates, this.getHistory()) ?? fallback;

    if (!selected) {
      this.remove();
      return;
    }

    this.querySelector('[data-homepage-recommendation-content]')?.replaceChildren(selected.content.cloneNode(true));
    this.dataset.selectedCollection = selected.dataset.homepageCollectionHandle;
  }

  getHistory() {
    const paths = [];

    try {
      const tracked = JSON.parse(localStorage.getItem('fuel_07_browsing_history') ?? '[]');
      if (Array.isArray(tracked)) paths.push(...tracked.slice().reverse());

      const stored = JSON.parse(localStorage.getItem('recently_viewed') ?? '[]');
      if (Array.isArray(stored)) paths.push(...stored.slice().reverse().map(({ productUrl }) => productUrl));
    } catch (error) {
      return paths.map((path) => this.normalizePath(path)).filter(Boolean);
    }

    return Array.from(new Set(paths.map((path) => this.normalizePath(path)).filter(Boolean)));
  }

  selectCollection(templates, history) {
    let selected;
    let selectedScore = 0;

    for (const template of templates) {
      const products = this.getCollectionProducts(template);
      const score = history.reduce((total, path, index) => {
        if (!products.has(path)) return total;
        return total + history.length - index;
      }, 0);

      if (score > selectedScore) {
        selected = template;
        selectedScore = score;
      }
    }

    return selected;
  }

  getCollectionProducts(template) {
    try {
      const data = template.content.querySelector('[data-homepage-collection-products]')?.textContent ?? '[]';
      return new Set(JSON.parse(data).map((url) => this.normalizePath(url)).filter(Boolean));
    } catch (error) {
      return new Set();
    }
  }

  normalizePath(url) {
    if (!url) return '';

    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }
}

if (!customElements.get('homepage-hero-marquee')) {
  customElements.define('homepage-hero-marquee', HomepageHeroMarquee);
}
if (!customElements.get('homepage-carousel')) customElements.define('homepage-carousel', HomepageCarousel);
if (!customElements.get('homepage-tabs')) customElements.define('homepage-tabs', HomepageTabs);
if (!customElements.get('homepage-recommendations')) {
  customElements.define('homepage-recommendations', HomepageRecommendations);
}

const GROWAVE_CARD_STYLE_ID = 'homepage-growave-card-a11y';
const GROWAVE_CARD_STYLE = `
  .gw-toggle-wishlist-product-card-button__counter {
    background-color: #d91f42 !important;
  }
`;

const applyHomepageGrowaveOverrides = () => {
  let pending = false;

  document.querySelectorAll('.homepage-product-card .gw-add-to-wishlist-product-card-placeholder').forEach((host) => {
    if (!host.shadowRoot) {
      pending = true;
      return;
    }

    if (!host.shadowRoot.getElementById(GROWAVE_CARD_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = GROWAVE_CARD_STYLE_ID;
      style.textContent = GROWAVE_CARD_STYLE;
      host.shadowRoot.append(style);
    }
  });

  return !pending;
};

if (!applyHomepageGrowaveOverrides()) {
  const growaveObserver = new MutationObserver(() => {
    if (applyHomepageGrowaveOverrides()) growaveObserver.disconnect();
  });

  growaveObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.setTimeout(() => growaveObserver.disconnect(), 10000);
}
